import React, { useEffect, useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, LayersIcon, Users } from "lucide-react";
import {
  Customer,
  getCustomersBySpendingTier,
  getCustomersByFrequency,
  getCustomersByLoyaltyTier,
} from "@/lib/db/customerDb";
import { DollarSign, Activity, Award } from "lucide-react";

const SegmentationTab = () => {
  const [spendingSegments, setSpendingSegments] = useState<Record<string, Customer[]>>({});
  const [frequencySegments, setFrequencySegments] = useState<Record<string, Customer[]>>({});
  const [loyaltySegments, setLoyaltySegments] = useState<Record<string, Customer[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [openSegments, setOpenSegments] = useState<Record<string, boolean>>({
    spending: true,
    frequency: false,
    loyalty: false,
  });

  useEffect(() => {
    const loadSegmentations = async () => {
      try {
        setIsLoading(true);
        const spending = await getCustomersBySpendingTier();
        const frequency = await getCustomersByFrequency();
        const loyalty = await getCustomersByLoyaltyTier();

        setSpendingSegments(spending);
        setFrequencySegments(frequency);
        setLoyaltySegments(loyalty);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load segmentation data", error);
        setIsLoading(false);
      }
    };

    loadSegmentations();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
    }).format(amount);
  };

  const toggleSegment = (segment: string) => {
    setOpenSegments(prev => ({
      ...prev,
      [segment]: !prev[segment]
    }));
  };

  const renderSegmentCard = (
    title: string, 
    customers: Customer[], 
    color: string
  ) => {
    return (
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex justify-between items-center">
            <div className="flex items-center">
              <Badge className={`mr-2 ${color}`}>{customers.length}</Badge>
              {title}
            </div>
            <Badge variant="outline">
              {formatCurrency(customers.reduce((sum, c) => sum + c.totalSpent, 0))}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground mb-2">
            {customers.length} customers in this segment
          </div>
          <div className="flex flex-wrap gap-1">
            {customers.slice(0, 5).map(customer => (
              <Badge key={customer.id} variant="secondary" className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {customer.name}
              </Badge>
            ))}
            {customers.length > 5 && (
              <Badge variant="outline">+{customers.length - 5} more</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <LayersIcon className="text-muted-foreground h-5 w-5" />
        <h2 className="text-xl font-semibold">Customer Segmentation</h2>
      </div>
      
      <div className="space-y-6">
        {/* Spending Tier Segmentation */}
        <Collapsible
          open={openSegments.spending}
          onOpenChange={() => toggleSegment('spending')}
          className="border rounded-lg px-4"
        >
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="flex justify-between w-full p-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                <span className="font-semibold">Spending Tier Segmentation</span>
              </div>
              {openSegments.spending ? <ChevronUp /> : <ChevronDown />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="p-4 pt-0">
            <div className="grid gap-4 md:grid-cols-3">
              {renderSegmentCard("High Spenders", spendingSegments['High Spenders'] || [], "bg-green-100 text-green-800")}
              {renderSegmentCard("Medium Spenders", spendingSegments['Medium Spenders'] || [], "bg-blue-100 text-blue-800")}
              {renderSegmentCard("Low Spenders", spendingSegments['Low Spenders'] || [], "bg-orange-100 text-orange-800")}
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        {/* Shopping Frequency Segmentation */}
        <Collapsible
          open={openSegments.frequency}
          onOpenChange={() => toggleSegment('frequency')}
          className="border rounded-lg px-4"
        >
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="flex justify-between w-full p-4">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                <span className="font-semibold">Shopping Frequency Segmentation</span>
              </div>
              {openSegments.frequency ? <ChevronUp /> : <ChevronDown />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="p-4 pt-0">
            <div className="grid gap-4 md:grid-cols-3">
              {renderSegmentCard("Frequent Shoppers", frequencySegments['Frequent'] || [], "bg-violet-100 text-violet-800")}
              {renderSegmentCard("Regular Shoppers", frequencySegments['Regular'] || [], "bg-indigo-100 text-indigo-800")}
              {renderSegmentCard("Occasional Shoppers", frequencySegments['Occasional'] || [], "bg-pink-100 text-pink-800")}
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        {/* Loyalty Tier Segmentation */}
        <Collapsible
          open={openSegments.loyalty}
          onOpenChange={() => toggleSegment('loyalty')}
          className="border rounded-lg px-4"
        >
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="flex justify-between w-full p-4">
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                <span className="font-semibold">Loyalty Tier Segmentation</span>
              </div>
              {openSegments.loyalty ? <ChevronUp /> : <ChevronDown />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="p-4 pt-0">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {loyaltySegments['Platinum'] && renderSegmentCard("Platinum Members", loyaltySegments['Platinum'], "bg-purple-100 text-purple-800")}
              {loyaltySegments['Gold'] && renderSegmentCard("Gold Members", loyaltySegments['Gold'], "bg-yellow-100 text-yellow-800")}
              {loyaltySegments['Silver'] && renderSegmentCard("Silver Members", loyaltySegments['Silver'], "bg-gray-100 text-gray-800")}
              {loyaltySegments['Regular'] && renderSegmentCard("Regular Members", loyaltySegments['Regular'], "bg-slate-100 text-slate-800")}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
};

export default SegmentationTab;
